// import {
//   queryParameters,
//   fetchJson,
// } from '@sebastien-cleany/admin-on-rest/lib/util/fetch';
import { stringify } from 'query-string';
import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  DELETE,
  DELETE_MANY,
  fetchUtils,
} from 'react-admin';

const API_URL = 'api';

// Resources whose PostgREST primary key is NOT the conventional `id` column.
// react-admin always works with an `id` field internally, so for these
// resources we expose the real key under `id` (read path) and translate
// `id=eq.` filters back to the real key column (write/get-one path).
// `cleaner_profile` is a 1:1-per-person table keyed on the silae matricule
// and has no surrogate `id` column.
const PRIMARY_KEYS = {
  cleaner_profile: 'silae_id',
};

const primaryKeyOf = (resource) => {
  return PRIMARY_KEYS[resource] || 'id';
};

const operators = {
  between: /between\(([^,]+), ?([^,]+)\)/,
  lte: /\blte\(([^)]+)\)/,
  gte: /\bgte\(([^)]+)\)/,
  in: /\bin\(([^)]+)\)/,
  neq: /\bneq\(([^)]+)\)/,
  // Matches a filter key that IS a full PostgREST `or(...)` expression, e.g.
  // `or(and(type.eq.Récurrent,active.is.true),start_date.gt.2026-07-02)`.
  // Unlike the other operators, the whole key is the PostgREST-ready value —
  // it is forwarded as-is to the `or=` query param (see ContractAutocompleteInput).
  or: /^or\(.*\)$/,
};

// Synthetic, UI-only filter keys that drive client-side filter logic but are
// NOT columns on any view/table. They must never be forwarded to PostgREST as
// query params (otherwise PostgREST rejects them as unknown columns, e.g.
// `column person_vivier.show_non_conforme does not exist`). See Vivier List.
const SYNTHETIC_FILTER_KEYS = ['show_non_conforme'];

/**
 * Maps admin-on-rest queries to a postgrest API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_MANY_REFERENCE
 *              => GET http://my.api.url/posts/2
 * GET_LIST     => GET http://my.api.url/posts?order=title.asc
 * GET_ONE      => GET http://my.api.url/posts?id=eq.123
 * GET_MANY     => GET http://my.api.url/posts?id=in.(123,456,789)
 * UPDATE       => PATCH http://my.api.url/posts?id=eq.123
 * CREATE       => POST http://my.api.url/posts
 * DELETE       => DELETE http://my.api.url/posts?id=eq.123
 * DELETE_MANY  => DELETE_MANY http://my.api.url/posts?id=in.(123,456,789)
 */
/* eslint no-param-reassign: ["error", { "props": false }] */
const convertFilters = (filters) => {
  const rest = {};

  Object.keys(filters).map((key) => {
    if (SYNTHETIC_FILTER_KEYS.includes(key)) {
      // UI-only key (e.g. Vivier `show_non_conforme` toggle): swallow it so it
      // never becomes a `?key=...` query param against PostgREST.
      return true;
    }
    let operator;
    let operatorType;
    Object.keys(operators).forEach((item) => {
      const reg = operators[item].exec(key);
      if (reg) {
        operator = reg;
        operatorType = item;
      }
    });

    if (operator) {
      switch (operatorType) {
        case 'between': {
          const [, from, to] = operator;
          rest[from] = `lte.${filters[key].replace(/:/, '')}`;
          rest[to] = `gte.${filters[key].replace(/:/, '')}`;
          break;
        }
        case 'gte':
        case 'lte': {
          const [, filter] = operator;
          rest[filter] = `${operatorType}.${filters[key].replace(/:/, '')}`;
          break;
        }
        case 'in': {
          const [, filter] = operator;

          if (!filters[key] || !Array.isArray(filters[key])) {
            break;
          }

          rest[filter] = `${operatorType}.(${filters[key].toString()})`;
          break;
        }
        case 'neq': {
          const [, filter] = operator;
          rest[filter] = `${operatorType}.${filters[key]}`;
          break;
        }
        case 'or': {
          // The key itself is the full PostgREST expression, including the
          // outer parens PostgREST requires on `or=(...)`. Forward it as the
          // `or` query param verbatim (no per-column rest[...] entry).
          rest.or = key.slice('or'.length);
          break;
        }
        default:
          break;
      }
      return true;
    }
    switch (typeof filters[key]) {
      case 'string':
        if (key === 'search') {
          rest[key] = `ilike.*${filters[key]
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/ /g, '*')}*`;
        } else if (key.endsWith('_id') || key === 'id') {
          rest[key] = `eq.${filters[key]}`;
        } else {
          rest[key] = `ilike.%${filters[key]}%`;
        }
        break;

      case 'boolean':
        rest[key] = `is.${filters[key]}`;
        break;

      case 'undefined':
        rest[key] = 'is.null';
        break;

      case 'number':
        rest[key] = `eq.${filters[key]}`;
        break;

      default:
        if (Array.isArray(filters[key])) {
          rest[key] = `cs.{${filters[key].join().replace(/:/, '')}}`;
          break;
        }
        rest[key] = `ilike.*${filters[key].toString()}*`;
        break;
    }
    return true;
  });
  return rest;
};

// Columns that are server-managed and must never appear in an upsert body for a
// resource whose real primary key is NOT `id` (see PRIMARY_KEYS). The records
// loaded by react-admin carry the raw row, including the serial `id` PK and the
// `created_at`/`updated_at` timestamps. When that record is spread back into a
// CREATE payload (e.g. cleaner_profile availability merge), `id` leaks into the
// POST body and the INSERT branch of the upsert hits `cleaner_profile_pkey`
// (ON CONFLICT (silae_id) does NOT catch a conflict on the `id` column) → 23505.
// Stripping these lets the upsert UPDATE the matched row (or INSERT with a fresh
// nextval `id`) without ever touching server-owned columns.
const SERVER_MANAGED_FIELDS = ['id', 'created_at', 'updated_at'];

const stripServerManagedFields = (data) => {
  if (!data) {
    return data;
  }
  const cleaned = {};
  Object.keys(data).forEach((key) => {
    if (!SERVER_MANAGED_FIELDS.includes(key)) {
      cleaned[key] = data[key];
    }
  });
  return cleaned;
};

const convertData = (data) => {
  const converted = {};

  if (!data) {
    return data;
  }

  Object.keys(data).forEach((key) => {
    if (data[key] instanceof Array) {
      converted[key] = JSON.stringify(data[key])
        .replace('[', '{')
        .replace(']', '}');
      return;
    }

    converted[key] = data[key];
  });

  return converted;
};

/**
 * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
 * @param {String} resource Name of the resource to fetch, e.g. 'posts'
 * @param {Object} params The REST request params, depending on the type
 * @returns {Object} { url, options } The HTTP request parameters
 */
const convertDataProviderRequestToHTTP = (type, resource, params) => {
  let url = '';
  const pk = primaryKeyOf(resource);
  const options = {};
  options.headers = new Headers();
  options.headers.set('Access-Control-Allow-Origin', '*');
  options.credentials = 'same-origin';
  switch (type) {
    case GET_LIST:
    case GET_MANY_REFERENCE: {
      const filters = {};

      Object.assign(filters, params.filter);
      const { field, order } = params.sort;
      const { page, perPage } = params.pagination;
      options.headers.set('Range-Unit', 'items');
      options.headers.set(
        'Range',
        `${(page - 1) * perPage}-${page * perPage - 1}`,
      );
      options.headers.set('Prefer', 'count=exact');

      if (type === GET_MANY_REFERENCE) {
        filters[params.target] = params.id;
      }

      const query = {};
      if (field) {
        const fieldParts = field.split(',');
        const orderParts = order.split(',');

        if (fieldParts.length !== orderParts.length) {
          throw new Error(
            `Sort should have the same number of field(${fieldParts.length}) and order(${orderParts.length})`,
          );
        }

        query.order = fieldParts
          .map((fieldPart, index) => {
            return `${fieldPart.trim()}.${orderParts[index]
              .trim()
              .toLowerCase()}`;
          })
          .join(',');
      }

      Object.assign(query, convertFilters(filters));
      url = `${API_URL}/${resource}?${stringify(query)}`;
      break;
    }
    case GET_ONE:
      url = `${API_URL}/${resource}?${pk}=eq.${params.id}`;
      break;
    case GET_MANY: {
      url = `${API_URL}/${resource}?${pk}=in.(${params.ids.join(',')})`;
      break;
    }
    case UPDATE:
      url = `${API_URL}/${resource}?${pk}=eq.${params.id}`;
      options.method = 'PATCH';
      options.body = JSON.stringify(convertData(params.data));
      break;
    case CREATE:
      url = `${API_URL}/${resource}`;
      if (pk !== 'id') {
        // Resources keyed on a natural/business key (see PRIMARY_KEYS, e.g.
        // cleaner_profile keyed on silae_id) are pre-seeded by backfills: a row
        // already exists for every key. A plain POST would violate the pkey
        // unique constraint. Make CREATE an idempotent upsert on that key
        // (PostgREST `Prefer: resolution=merge-duplicates` resolves the
        // conflict by merging into the existing row).
        url = `${API_URL}/${resource}?on_conflict=${pk}`;
        options.headers.set(
          'Prefer',
          'return=representation,resolution=merge-duplicates',
        );
        // The upsert body must carry only the business key + edited columns.
        // Drop the server-managed columns (serial `id`, timestamps) that ride
        // along when a loaded record is spread into the payload, otherwise the
        // INSERT path conflicts on `id` (cleaner_profile_pkey), which
        // ON CONFLICT (silae_id) cannot resolve → 23505.
        options.body = JSON.stringify(
          convertData(stripServerManagedFields(params.data)),
        );
      } else {
        options.headers.set('Prefer', 'return=representation');
        options.body = JSON.stringify(convertData(params.data));
      }
      options.method = 'POST';
      break;
    case DELETE:
      url = `${API_URL}/${resource}?${pk}=eq.${params.id}`;
      options.method = 'DELETE';
      break;
    case DELETE_MANY:
      url = `${API_URL}/${resource}?${pk}=in.(${params.ids.join(',')})`;
      options.method = 'DELETE';
      break;
    default:
      throw new Error(`Unsupported fetch action type ${type}`);
  }
  return { url, options };
};

/**
 * @param {Object} response HTTP response from fetch()
 * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
 * @param {String} resource Name of the resource to fetch, e.g. 'posts'
 * @param {Object} params The REST request params, depending on the type
 * @returns {Object} REST response
 */
const convertHTTPResponseToDataProvider = (
  response,
  type,
  resource,
  params,
) => {
  const { headers, json } = response;
  const pk = primaryKeyOf(resource);
  // react-admin requires every record to expose an `id`. For resources whose
  // real primary key is not `id` (see PRIMARY_KEYS), alias it onto `id`.
  const withId = (row) => {
    if (pk === 'id' || !row) {
      return row;
    }
    return { ...row, id: row[pk] };
  };
  switch (type) {
    case GET_LIST:
    case GET_MANY_REFERENCE: {
      if (!headers.has('content-range')) {
        throw new Error(
          'The Content-Range header is missing in the HTTP Response. The simple REST client expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?',
        );
      }
      const maxInPage =
        parseInt(
          headers
            .get('content-range')
            .split('/')[0]
            .split('-')
            .pop(),
          10,
        ) + 1;
      return {
        data: json.map((x) => {
          return withId(x);
        }),
        total:
          parseInt(
            headers
              .get('content-range')
              .split('/')
              .pop(),
            10,
          ) ||
          maxInPage ||
          0,
      };
    }
    case GET_MANY:
      // PostgREST returns a bare array; react-admin requires every record to
      // expose an `id`. For resources keyed on a natural key (see PRIMARY_KEYS,
      // e.g. cleaner_profile keyed on silae_id), alias it onto `id`.
      return {
        data: (json || []).map((x) => {
          return withId(x);
        }),
      };
    case CREATE:
    case GET_ONE:
      if (!json.length) {
        return {};
      }
      return { id: json[0][pk], data: { ...withId(json[0]) } };
    case DELETE:
      return { data: { id: params.id } };
    case DELETE_MANY:
      return { data: params.ids };
    default:
      if (!json) {
        return { data: Object.assign({}, params.data) };
      }
      return { data: json };
  }
};

/**
 * @param {string} type Request type, e.g GET_LIST
 * @param {string} resource Resource name, e.g. "posts"
 * @param {Object} payload Request parameters. Depends on the request type
 * @returns {Promise} the Promise for a REST response
 */
export default (type, resource, params) => {
  const { fetchJson } = fetchUtils;
  const { url, options } = convertDataProviderRequestToHTTP(
    type,
    resource,
    params,
  );
  return fetchJson(url, options).then((response) => {
    return convertHTTPResponseToDataProvider(response, type, resource, params);
  });
};
